import { useCallback, useEffect, useMemo, useState } from 'react';
import { AppDialog } from '../../../components/AppDialog';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { useNotify } from '../../../components/notifications';
import { useConfirm, useDeleteFlow, useSaveFlow } from '../../../components/confirm';
import { fmtLocalDateTime } from '../../../utils/datetime';
import { MESSAGE_FORM_NAME } from './messageLibraryConfig';
import {
  addTranslation, deleteTranslation, fetchMessage, updateTranslation,
} from './api';
import type { MessageRow, MessageTranslation } from './types';
import './messageLibrary.css';

interface Props {
  /** The message whose translations to manage; null closes the dialog. */
  message: MessageRow | null;
  /** The language vocabulary, served by the API so this list and the server-side
   *  validator can never drift. */
  languages: string[];
  onClose: () => void;
  /** Called after any change, so the list's translation count refreshes. */
  onChanged: () => void;
}

/**
 * A message's translations — the legacy `dbo.Translations` child rows.
 *
 * `dbo.Translations` is shared between Glossary and MessageEditor through two
 * separate associations, and ALPACA renders it as a child grid on the Message
 * Editor detail view with linking disabled (`AllowLink="False"
 * AllowUnlink="False"`), i.e. New and Delete only — which is what this offers.
 *
 * **Why a dialog and not a tab inside the detail view:** the shared
 * `<SettingsDetailPage>` has no generic child-grid slot, and every existing child
 * grid is wired in by its own flag inside that 3,000-line shared component. Adding
 * one would mean editing code every other detail form renders, which is outside
 * "add the new form". Reached from the Translations count in the list instead —
 * the count is the natural affordance, and nothing shared changes.
 *
 * ALPACA's `Language` enum is Chinese / French / Spanish, and a message carries at
 * most one translation per language (enforced server-side).
 */
export function TranslationsDialog({ message, languages, onClose, onChanged }: Props) {
  const notify = useNotify();
  const { confirm } = useConfirm();
  const runSave = useSaveFlow();
  const runDelete = useDeleteFlow();

  const [rows, setRows] = useState<MessageTranslation[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<MessageTranslation | null>(null);
  const [language, setLanguage] = useState('');
  const [text, setText] = useState('');

  const messageId = message?.oid ?? null;

  const load = useCallback(async () => {
    if (!messageId) return;
    setLoading(true);
    try {
      const d = await fetchMessage(messageId);
      setRows(d?.translations ?? []);
    } finally {
      setLoading(false);
    }
  }, [messageId]);

  useEffect(() => {
    if (!messageId) return;
    setEditing(null);
    setLanguage('');
    setText('');
    void load();
  }, [messageId, load]);

  /** The languages still available — a message holds at most one per language, so
   *  offering a taken one would only produce a server rejection. The row being
   *  edited keeps its own language in the list. */
  const available = useMemo(() => {
    const taken = new Set(rows.filter((r) => r.oid !== editing?.oid).map((r) => r.language));
    return languages.filter((l) => !taken.has(l));
  }, [languages, rows, editing]);

  const startNew = () => {
    const first = available[0];
    if (!first) {
      notify.warning('This message already has a translation in every language.');
      return;
    }
    setEditing({ oid: '', language: first, translation: '', lastUpdatedDate: null });
    setLanguage(first);
    setText('');
  };

  const startEdit = (row: MessageTranslation) => {
    setEditing(row);
    setLanguage(row.language ?? '');
    setText(row.translation ?? '');
  };

  const save = async () => {
    if (!messageId || !editing) return;
    if (!language) { notify.error('Language must not be empty.'); return; }
    if (!text.trim()) { notify.error('Translation must not be empty.'); return; }

    // The shared save flow: confirm → reason gate → audited call → "Saved
    // successfully." (standing rule 7). The reason prompt appears only when
    // Settings → Audit Trail Configuration has ReasonRequired on for this form.
    await runSave({
      formName: MESSAGE_FORM_NAME,
      auditAction: editing.oid ? 'edit' : 'create',
      save: async () => {
        if (editing.oid) await updateTranslation(messageId, editing.oid, language, text.trim());
        else await addTranslation(messageId, language, text.trim());
      },
      onSaved: async () => {
        setEditing(null);
        await load();
        onChanged();
      },
    });
  };

  const remove = async (row: MessageTranslation) => {
    if (!messageId) return;
    await runDelete({
      formName: MESSAGE_FORM_NAME,
      message: `Delete the ${row.language} translation?`,
      delete: async () => { await deleteTranslation(messageId, row.oid); },
      onDeleted: async () => { await load(); onChanged(); },
    });
  };

  const columns: AppColumn<MessageTranslation>[] = [
    { field: 'language', header: 'Language', width: 130 },
    { field: 'translation', header: 'Translation', width: 360 },
    {
      field: 'lastUpdatedDate',
      header: 'Last Updated',
      width: 150,
      text: (r) => fmtLocalDateTime(r.lastUpdatedDate),
      render: (r) => fmtLocalDateTime(r.lastUpdatedDate),
    },
    {
      field: 'actions',
      header: '',
      width: 110,
      sortable: false,
      filterable: false,
      noExport: true,
      render: (r) => (
        <span className="ml-rowacts">
          <button type="button" className="ml-linkcell" onClick={() => startEdit(r)}>Edit</button>
          <button type="button" className="ml-linkcell is-danger" onClick={() => void remove(r)}>Delete</button>
        </span>
      ),
    },
  ];

  return (
    <AppDialog
      open={message !== null}
      title={`Translations — ${message?.messageKey ?? ''}`}
      icon="ti-language"
      width={780}
      onClose={async () => {
        // Never lose an unsaved edit silently (standing rule 15). The dialog's own
        // in-memory editor is invisible to the shared tracker, so it asks here.
        if (editing && (text.trim() || editing.oid)) {
          const ok = await confirm({
            title: 'Unsaved changes',
            message: 'Unsaved changes will be lost. Do you want to proceed?',
            tone: 'danger',
          });
          if (!ok) return;
        }
        onClose();
      }}
      footer={<button type="button" className="ca-btn" onClick={onClose}>Close</button>}
    >
      <div className="ml-tr">
        <p className="ml-tr-msg">{message?.message}</p>

        <div className="ml-tr-bar">
          <button type="button" className="ca-btn" onClick={startNew}>
            <span className="tb-i"><i className="ti ti-plus" /></span>New
          </button>
        </div>

        <AppDataGrid<MessageTranslation>
          rows={rows}
          columns={columns}
          getRowId={(r) => r.oid}
          loading={loading}
          hideHeaderTools
          defaultPageSize={10}
          emptyText="No translations"
        />

        {editing && (
          <div className="ml-tr-edit">
            <label className="ml-assign-row">
              <span className="ml-assign-label">Language</span>
              <select
                className="sd-input"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
              >
                {available.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </label>

            <label className="ml-assign-row">
              <span className="ml-assign-label">Translation</span>
              <textarea
                className="sd-input ml-tr-text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="The translated wording"
              />
            </label>

            <div className="ml-tr-editbar">
              <button type="button" className="ca-btn ca-btn-solid" onClick={() => void save()}>Save</button>
              <button type="button" className="ca-btn" onClick={() => setEditing(null)}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    </AppDialog>
  );
}

export default TranslationsDialog;
